import { useState, useEffect, useRef } from 'react';
import { useBranding, fontStack } from '../../../hooks/useBranding';
import { authFetch } from '../../../api';
import { contrastRatio, ensureDark } from '../../../lib/color';
import { VOICE_LANGUAGES } from '../../../lib/voiceLanguages';

const FONTS = ['DM Sans', 'Inter', 'Roboto', 'Poppins', 'Lato', 'Nunito', 'Open Sans', 'Source Sans Pro', 'Raleway', 'Times New Roman', 'Calibri'];

function hexToRgb(hex: string) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  if (!m) return '255,255,255';
  return [m[1]!, m[2]!, m[3]!].map((h) => parseInt(h, 16)).join(',');
}
function contrastLabel(ratio: number) {
  if (ratio >= 7) return { text: 'Excellent', color: '#059669' };
  if (ratio >= 4.5) return { text: 'Good', color: '#059669' };
  if (ratio >= 3) return { text: 'Borderline — hard to read at small sizes', color: '#D97706' };
  return { text: 'Poor — text will be hard to read', color: '#DC2626' };
}
const NAV_TEXT_CANDIDATES = ['FFFFFF', 'F8FAFC', '0F172A', '111827'];

function ColorSwatch({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="lbl">{label}</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ width: 36, height: 36, borderRadius: 8, background: `#${value}`, border: '1px solid #E5E7EB', cursor: 'pointer', flexShrink: 0 }} />
        <input value={value} onChange={(e) => onChange(e.target.value.replace('#', ''))} placeholder="Hex without #" style={{ fontFamily: 'monospace' }} />
        <input type="color" value={`#${value}`} onChange={(e) => onChange(e.target.value.replace('#', ''))}
          style={{ width: 36, height: 36, padding: 2, border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer' }} />
      </div>
    </div>
  );
}

export default function BrandingSettingsTab() {
  const { branding, refresh } = useBranding();
  const [form, setForm] = useState<any>({});
  const [saving, setSave] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [uploading, setUL] = useState<Record<string, boolean>>({});
  const logoRef = useRef<HTMLInputElement>(null);
  const faviconRef = useRef<HTMLInputElement>(null);
  const bgRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setForm({ ...branding }); }, [branding]);

  const f = (k: string, v: unknown) => setForm((p: any) => ({ ...p, [k]: v }));

  // Only report "Saved" when the server accepted the change — this used to
  // show it for any response, so a rejected save looked like it worked and
  // the old values silently came back on the next load.
  const save = async () => {
    setSave(true); setSaveError(null);
    try {
      const res = await authFetch('/api/branding', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setSaveError(`Not saved (${res.status}): ${body.error || res.statusText || 'request failed'}`);
        return;
      }
      setSaved(true); setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      setSaveError(`Not saved: ${e instanceof Error ? e.message : 'network error'}`);
    } finally {
      setSave(false); refresh();
    }
  };

  const uploadAsset = async (type: string, file: File | null) => {
    if (!file) return;
    setUL((p) => ({ ...p, [type]: true }));
    const fd = new FormData(); fd.append('file', file);
    const res = await authFetch(`/api/branding/upload/${type}`, { method: 'POST', body: fd });
    const data = await res.json();
    if (data.url) setForm((p: any) => ({ ...p, [`${type === 'login_bg' ? 'login_bg' : type}_url`]: `/api/branding/asset/${type}?v=${Date.now()}` }));
    refresh(); setUL((p) => ({ ...p, [type]: false }));
  };

  const navText = form.nav_text_color || 'FFFFFF';
  const navy = form.navy_color || '0B1D33';

  const PreviewBadge = () => (
    <div style={{ padding: '8px 16px', background: `#${navy}`, color: '#fff', borderRadius: 'var(--radius)', display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      {form.logo_url ? <img src={form.logo_url} alt="logo" style={{ height: 28, objectFit: 'contain' }} /> : <span style={{ fontSize: 18 }}>🏥</span>}
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, fontFamily: fontStack(form.font_choice) }}>{form.hospital_name}</div>
        <div style={{ fontSize: 10, color: `#${form.accent_color || '00D9A5'}`, letterSpacing: '1px', textTransform: 'uppercase' }}>{form.tagline}</div>
      </div>
    </div>
  );

  const NavPreview = () => (
    <div style={{ background: `#${navy}`, borderRadius: 'var(--radius)', padding: '10px 0', marginBottom: 12, width: 200 }}>
      {['Dashboard', 'Intake', 'Visit workflow'].map((label, i) => (
        <div key={label} style={{
          padding: '8px 16px', fontSize: 13, fontWeight: i === 0 ? 700 : 600,
          color: i === 0 ? `#${navText}` : `rgba(${hexToRgb(navText)},.68)`,
          background: i === 0 ? `rgba(${hexToRgb(form.accent_color || '00D9A5')},.16)` : 'transparent',
          borderLeft: i === 0 ? `2px solid #${form.accent_color || '00D9A5'}` : '2px solid transparent',
        }}>{label}</div>
      ))}
    </div>
  );

  return (
    <div className="fade-in" style={{ maxWidth: 700 }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>Live preview</div>
        <PreviewBadge />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #F3F4F6' }}>Identity</div>
        <div className="grid2" style={{ marginBottom: 14 }}>
          <div style={{ gridColumn: '1/-1' }}><label className="lbl">Hospital / clinic name</label><input value={form.hospital_name || ''} onChange={(e) => f('hospital_name', e.target.value)} /></div>
          <div style={{ gridColumn: '1/-1' }}><label className="lbl">Tagline</label><input value={form.tagline || ''} onChange={(e) => f('tagline', e.target.value)} /></div>
          <div style={{ gridColumn: '1/-1' }}><label className="lbl">Footer text</label><input value={form.footer_text || ''} onChange={(e) => f('footer_text', e.target.value)} /></div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #F3F4F6' }}>Letterhead (used on generated documents)</div>
        <div className="grid2">
          <div style={{ gridColumn: '1/-1' }}><label className="lbl">Address</label><input value={form.address || ''} onChange={(e) => f('address', e.target.value)} placeholder="145, Palayakottai Road, Kangayam-638 701" /></div>
          <div><label className="lbl">Phone</label><input value={form.phone || ''} onChange={(e) => f('phone', e.target.value)} placeholder="04257 - 221416, 88834 06063" /></div>
          <div><label className="lbl">Website</label><input value={form.website || ''} onChange={(e) => f('website', e.target.value)} placeholder="www.example.in" /></div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #F3F4F6' }}>Colors</div>
        <div className="grid3" style={{ marginBottom: 16 }}>
          <ColorSwatch label="Primary (teal)" value={form.primary_color || '0B5FD6'} onChange={(v) => f('primary_color', v)} />
          <ColorSwatch label="Accent" value={form.accent_color || '00D9A5'} onChange={(v) => f('accent_color', v)} />
          <ColorSwatch label="Navy (sidebar)" value={form.navy_color || '0B1D33'} onChange={(v) => f('navy_color', v)} />
        </div>

        {/* Not a stored field — the sidebar and the Consultation stepper's
            compact bar both paint with a forced-dark version of Navy
            (ensureDark(), same hue, deepened only as far as needed), rather
            than the raw color above, so a bright/light navy_color still
            reads as fixed chrome instead of a bug. Shown here so choosing
            Navy also shows what those bars will actually look like. */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: `#${ensureDark(navy)}`, border: '1px solid #E5E7EB', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>Dark chrome (auto)</div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>
              Sidebar &amp; the Consultation stepper bar use this — Navy above, forced dark (#{ensureDark(navy)}) if it isn't already.
            </div>
          </div>
        </div>

        <div className="grid2" style={{ alignItems: 'start' }}>
          <div>
            <ColorSwatch label="Sidebar nav text" value={navText} onChange={(v) => f('nav_text_color', v)} />
            {(() => {
              const ratio = contrastRatio(navText, navy);
              const rating = contrastLabel(ratio);
              return (
                <div style={{ marginTop: 8, fontSize: 11, color: rating.color }}>
                  Contrast against navy: {ratio.toFixed(1)}:1 — {rating.text}
                </div>
              );
            })()}
          </div>
          <div>
            <label className="lbl">Suggestions for this navy</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[NAV_TEXT_CANDIDATES.slice(0, 2), NAV_TEXT_CANDIDATES.slice(2, 4)]
                .map((group) => group.map((hex) => ({ hex, ratio: contrastRatio(hex, navy) })).sort((a, b) => b.ratio - a.ratio)[0]!)
                .map((c) => (
                  <button key={c.hex} type="button" onClick={() => f('nav_text_color', c.hex)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8, border: c.hex === navText ? '2px solid var(--teal)' : '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: 11 }}>
                    <span style={{ width: 16, height: 16, borderRadius: 4, background: `#${c.hex}`, border: '1px solid #E5E7EB' }} />
                    #{c.hex} ({c.ratio.toFixed(1)}:1{c.ratio >= 4.5 ? ' ✓' : ''})
                  </button>
                ))}
            </div>
            <div style={{ marginTop: 10 }}>
              <NavPreview />
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #F3F4F6' }}>Typography</div>
        <div style={{ maxWidth: 300 }}>
          <label className="lbl">Font family</label>
          <select value={form.font_choice || 'DM Sans'} onChange={(e) => f('font_choice', e.target.value)}>
            {FONTS.map((fn) => <option key={fn}>{fn}</option>)}
          </select>
          <div style={{ marginTop: 8, padding: '10px 14px', background: '#F9FAFB', borderRadius: 'var(--radius)', fontSize: 14, fontFamily: fontStack(form.font_choice) }}>
            The quick brown fox jumps over the lazy dog
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #F3F4F6' }}>Voice input</div>
        <div style={{ maxWidth: 320 }}>
          <label className="lbl">Speech-recognition language</label>
          <select value={form.default_voice_language || 'en-IN'} onChange={(e) => f('default_voice_language', e.target.value)}>
            {VOICE_LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
          <div style={{ marginTop: 8, fontSize: 11.5, color: '#6B7280', lineHeight: 1.6 }}>
            The language every 🎙 mic across the app listens for — Patient Intake, Consultation, Prescription, Case Sheet forms, and anywhere else voice input is offered. One setting for the whole hospital, instead of picking it separately on every screen.
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #F3F4F6' }}>Assets</div>
        <div className="grid3">
          {[
            { key: 'logo', label: 'Logo', ref: logoRef, hint: 'PNG/SVG, shown in header' },
            { key: 'favicon', label: 'Favicon', ref: faviconRef, hint: 'ICO/PNG 32×32' },
            { key: 'login_bg', label: 'Login background', ref: bgRef, hint: 'JPG/PNG, login page bg' },
          ].map((asset) => (
            <div key={asset.key}>
              <label className="lbl">{asset.label}</label>
              <div style={{ border: '2px dashed #E5E7EB', borderRadius: 'var(--radius)', padding: 12, textAlign: 'center', cursor: 'pointer' }} onClick={() => asset.ref.current?.click()}>
                {form[`${asset.key}_url`] ? <img src={form[`${asset.key}_url`]} alt={asset.label} style={{ height: 40, objectFit: 'contain', marginBottom: 4 }} /> : <div style={{ fontSize: 24, marginBottom: 4 }}>🖼</div>}
                <div style={{ fontSize: 11, color: '#9CA3AF' }}>{uploading[asset.key] ? 'Uploading…' : asset.hint}</div>
              </div>
              <input ref={asset.ref} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => uploadAsset(asset.key, e.target.files?.[0] || null)} />
            </div>
          ))}
        </div>
      </div>

      <button className="btn-primary" onClick={save} disabled={saving} style={{ minWidth: 140 }}>
        {saved ? '✓ Saved!' : saving ? 'Saving…' : 'Save branding'}
      </button>
      {saveError && <p role="alert" className="mt-2 text-sm text-destructive">{saveError}</p>}
    </div>
  );
}
